import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import LoadingModal from './components/modals/LoadingModal';
import ContaModal from './components/modals/ContaModal';
import AgenciaModal from './components/modals/AgenciaModal';
import usePagination10 from './components/hooks/usePagination10';
import useReferenceData from './components/hooks/useReferenceData';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import { fndeService } from './api/fndeService';
import Toolbar from './components/Toolbar';

const emptyForm = { idPrograma: '', idEscola: '', numConta: '', idBanco: '', idAgencia: '' };
const emptyAgenciaForm = { idEscola: '', idBanco: '', idAgencia: '', cidade: '', uf: '' };

function Contas() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '' });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [missingAgencias, setMissingAgencias] = useState([]);
  const [showAgenciaModal, setShowAgenciaModal] = useState(false);
  const [agenciaForm, setAgenciaForm] = useState(emptyAgenciaForm);
  const [ufs, setUfs] = useState([]);
  const [cidadesAgencia, setCidadesAgencia] = useState([]);
  const ano = localStorage.getItem('ano') || new Date().getFullYear().toString();
  const [filtroPrograma, setFiltroPrograma] = useState("");

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { programas, bancos, agencias } = useReferenceData(token, cnpj, atualizar);
  // Ordena items por número da conta (numérico antes do traço)
  function parseContaNum(numConta) {
    const m = String(numConta).match(/^(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  }
  const sortedItems = [...items].sort((a, b) => {
    const nA = parseContaNum(a.numConta);
    const nB = parseContaNum(b.numConta);
    if (nA !== nB) return nA - nB;
    return String(a.numConta).localeCompare(String(b.numConta));
  });
  const filteredItems = filtroPrograma
    ? sortedItems.filter(item => String(item.idPrograma) === String(filtroPrograma))
    : sortedItems;
  const displayed = filteredItems.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  useEffect(() => {
    axios.get(`${API_BASE_URL.UF}/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUfs(r.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (agenciaForm.uf) {
      axios.get(`${API_BASE_URL.MUNICIPIO}/uf/${agenciaForm.uf}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCidadesAgencia(r.data)).catch(() => setCidadesAgencia([]));
    } else {
      setCidadesAgencia([]);
    }
  }, [agenciaForm.uf, token]);

  useEffect(() => {
    if (!cnpj) { showDefaultModal('error', 'Escola não selecionada.'); setIsLoading(false); return; }
    setIsLoading(true);
    axios.get(`${API_BASE_URL.CONTA}/findByEscola`, {
      params: { idEscola: cnpj }, headers: { Authorization: `Bearer ${token}` }
    }).then(r => setItems(r.data))
      .catch(() => showDefaultModal('error', 'Erro ao carregar contas.'))
      .finally(() => setIsLoading(false));
  }, [token, cnpj, atualizar]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => {
      if (name === 'idBanco') return { ...f, idBanco: value, idAgencia: '' };
      return { ...f, [name]: value };
    });
  }
  function handleNew() { setForm({ ...emptyForm, idEscola: cnpj || '' }); setShowModal(true); }
  function handleEdit() { if (selected.length !== 1) return; setForm({ ...selected[0] }); setShowModal(true); }

  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.CONTA}/save`, form, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]); // Limpa seleção após salvar
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar conta.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () =>
      Promise.all(selected.map(s => axios.delete(`${API_BASE_URL.CONTA}/delete`, {
        params: { idPrograma: s.idPrograma, idEscola: s.idEscola, numConta: s.numConta },
        headers: { Authorization: `Bearer ${token}` }
      }))).then(() => { setSelected([]); setAtualizar(a => !a); })
        .catch(() => showDefaultModal('error', 'Erro ao excluir conta.'))
    );
  }

  function handleAgenciaChange(e) {
    const { name, value } = e.target;
    setAgenciaForm(f => {
      const updated = { ...f, [name]: value };
      if (name === 'uf') updated.cidade = '';
      return updated;
    });
  }

  function handleAgenciaSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.AGENCIA}/save`, agenciaForm, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        const remaining = missingAgencias.filter(a => !(a.idBanco === agenciaForm.idBanco && a.idAgencia === agenciaForm.idAgencia));
        setMissingAgencias(remaining);
        setShowAgenciaModal(false);
        setAtualizar(a => !a);
        if (remaining.length === 0) {
          showDefaultModal('success', 'Todas as agências foram cadastradas. Clique em "Importar FNDE" novamente.');
        }
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar agência.'));
  }

  function openAgenciaModal(idBanco, idAgencia) {
    setAgenciaForm({ idEscola: cnpj || '', idBanco, idAgencia, cidade: '', uf: '' });
    setShowAgenciaModal(true);
  }

  function parseMissingAgencias(erros) {
    const regex = /banco\s+(\S+),\s+ag[eê]ncia\s+(\S+)/i;
    const map = new Map();
    (erros || []).forEach(msg => {
      const m = msg.match(regex);
      if (m) {
        const key = `${m[1]}_${m[2]}`;
        if (!map.has(key)) map.set(key, { idBanco: m[1], idAgencia: m[2] });
      }
    });
    return Array.from(map.values());
  }

  async function handleImportarFnde() {
    if (!cnpj) return;
    setIsImporting(true);
    try {
      const mes = `12-${String(parseInt(ano) - 1)}`;
      const resp = await fndeService.buscarSaldoEntidade(mes, cnpj);
      if (!resp.data.sucesso) { showDefaultModal('error', `Erro FNDE: ${resp.data.erro}`); return; }
      const dados = resp.data.dados || [];
      if (!dados.length) { showDefaultModal('warning', 'Nenhum dado retornado pelo FNDE.'); return; }
      const keys = Object.keys(dados[0]);
      const colConta   = keys.find(k => /conta/i.test(k) && !/saldo|raz/i.test(k));
      const colBanco   = keys.find(k => /^banco$/i.test(k));
      const colAgencia = keys.find(k => /ag[eê]ncia/i.test(k));
      const colProgram = keys.find(k => /programa/i.test(k));
      if (!colConta) { showDefaultModal('error', 'Coluna "Conta" não encontrada nos dados FNDE.'); return; }
      const payload = dados
        .map(row => ({ numConta: row[colConta] || '', idBanco: row[colBanco] || '', idAgencia: row[colAgencia] || '', programa: row[colProgram] || '' }))
        .filter(c => c.numConta);
      const r = await axios.post(
        `${API_BASE_URL.CONTA}/importar-fnde?idEscola=${encodeURIComponent(cnpj)}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { salvas, ignoradas, erros } = r.data;
      const faltantes = parseMissingAgencias(erros);
      if (faltantes.length > 0) {
        setMissingAgencias(faltantes);
        const lista = faltantes.map(a => `Banco ${a.idBanco} / Agência ${a.idAgencia}`).join('\n');
        showDefaultModal('warning', `${ignoradas} conta(s) ignorada(s) — agência(s) não cadastrada(s):\n\n${lista}\n\nCadastre a agência e importe novamente.`);
      } else {
        const msg = `${salvas} conta(s) importada(s), ${ignoradas} ignorada(s).` +
                    (erros?.length ? `\n\nAvisos:\n${erros.join('\n')}` : '');
        showDefaultModal(salvas > 0 ? 'success' : 'warning', msg);
      }
      if (salvas > 0) setAtualizar(a => !a);
    } catch (e) {
      showDefaultModal('error', `Erro ao importar contas: ${e.message}`);
    } finally {
      setIsImporting(false);
    }
  }

  function toggleSelect(item) {
    const key = `${item.idPrograma}_${item.idEscola}_${item.numConta}`;
    setSelected(prev => prev.find(s => `${s.idPrograma}_${s.idEscola}_${s.numConta}` === key) ? [] : [item]);
  }

  function progLabel(idPrograma) {
    const p = (programas || []).find(p => String(p.id) === String(idPrograma));
    return p ? p.descricao : idPrograma;
  }

  function bancoLabel(idBanco) {
    const b = (bancos || []).find(b => String(b.id) === String(idBanco));
    return b ? b.nome : idBanco;
  }

  return (
    <Layout>
      <LoadingModal show={isImporting} />
      <ResponsiveShadow>
        <PageHeader title="CONTAS BANCÁRIAS" icon="bi-credit-card" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
            <button className="btn btn-primary btn-sm botao-igual centralizar-se-impar" onClick={handleImportarFnde} disabled={isImporting}><i className="bi bi-database-add me-1"></i>Importar FNDE</button>
            <div className="toolbar-search">
              <select className="form-select form-select-sm" value={filtroPrograma} onChange={e => setFiltroPrograma(e.target.value)}>
                <option value="">Todos os programas</option>
                {programas.map(p => <option key={p.id} value={p.id}>{p.descricao}</option>)}
              </select>
            </div>

          </Toolbar>
          {missingAgencias.length > 0 && (
            <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-2" style={{ fontSize: '0.82rem' }}>
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>
                <strong>{missingAgencias.length} agência(s) não cadastrada(s):</strong>{' '}
                {missingAgencias.map((a, i) => (
                  <span key={`${a.idBanco}_${a.idAgencia}`}>
                    {i > 0 && ', '}
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline" style={{ fontSize: '0.82rem' }} onClick={() => openAgenciaModal(a.idBanco, a.idAgencia)}>
                      Banco {a.idBanco} / Ag. {a.idAgencia}
                    </button>
                  </span>
                ))}
              </span>
            </div>
          )}
          <TableContainer isLoading={false}>
                              <DataTable head={<tr>
                  <th style={{ width: '3rem' }}>#</th><th>Nº Conta</th><th>Banco</th><th>Agência</th><th>Programa</th><th>Debitar</th>
                </tr>}>
                <tbody>
                  {displayed.map((item, idx) => {
                    const key = `${item.idPrograma}_${item.idEscola}_${item.numConta}`;
                    return (
                      <tr key={key} className={selected.find(s => `${s.idPrograma}_${s.idEscola}_${s.numConta}` === key) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                        <td>{setPagination.startIndex + idx + 1}</td>
                        <td>{item.numConta}</td>
                        <td>{bancoLabel(item.idBanco)}</td>
                        <td>{item.idAgencia}</td>
                        <td>{progLabel(item.idPrograma)}</td>
                        <td>{item.debitar}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <ContaModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} programas={programas} bancos={bancos} agencias={agencias} />
      <AgenciaModal show={showAgenciaModal} onClose={() => setShowAgenciaModal(false)} onSubmit={handleAgenciaSave} form={agenciaForm} handleChange={handleAgenciaChange} bancos={bancos} ufs={ufs} cidades={cidadesAgencia} title="CADASTRAR AGÊNCIA FNDE" />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Contas;
